import { D3RectData, d3renderPathFilled, d3renderRect, d3renderText, D3TextData } from './chart.d3';
import type { ChartManager } from './chart.manager';

import { cloneObject, roundTo, unique } from '@23degrees/shared-code/utilities';

import { getOldFields } from '@23degrees/shared-code/config/dimension.config';
import chroma from 'chroma-js';
import { select } from 'd3-selection';
import { getCSSFont } from './chart.utils';
import { overlayText } from './chart.overlay';
import { DotTypes } from '@23degrees/shared-code/config/constants';
import type { CustomDots, Quill } from '@23degrees/shared-code/models';
import { capitalize, isIterable, toCamelCase } from '@23degrees/shared-code/utilities/format';
import * as Markers from '@23degrees/packages/map-overlays2/components/marker-icon-selector2/markers.constants';

// hex transitions suck, alpha would need svg fill #rrggbb and fill-opacity #a
const color2css = (color) => chroma(color).hex();

const fixLineHeight = (obj: any) => {
  if (typeof obj === 'number') return obj;
  return 1.25;
};

/**
 * [create meta data for legend]
 * needs to be called before updateLegend
 * @param  legendWidth  [with of the legend container]
 * @return legendHeight [calculated height for the legend container]
 */
export const processLegend = (chartManager: ChartManager, legendWidth: number): any => {
  const { type, typeSpecific } = chartManager.content;
  const { labelField } = getOldFields(chartManager.content.type, chartManager.content?.typeSpecific?.dimensions);
  const { legend, dots } = typeSpecific.chart;
  const { colors2 } = typeSpecific.style;

  const dynamicStyle = cloneObject(legend.style);
  dynamicStyle.size = chartManager.getDynamicFontSize(legend.style, legendWidth, legend.fontScale);

  const { margin, padding } = dynamicStyle;
  const widthMargin = chartManager.getStyleInPx(margin, 'left', true) + chartManager.getStyleInPx(margin, 'right', true);
  const widthPadding = chartManager.getStyleInPx(padding, 'left', true) + chartManager.getStyleInPx(padding, 'right', true);
  const widthMarginPadding = widthMargin + widthPadding;

  const heightMargin = chartManager.getStyleInPx(margin, 'top', true) + chartManager.getStyleInPx(margin, 'bottom', true);
  const heightPadding = chartManager.getStyleInPx(padding, 'top', true) + chartManager.getStyleInPx(padding, 'bottom', true);
  const heightMarginPadding = heightMargin + heightPadding;

  const availableWidth = legendWidth - widthMarginPadding;

  const mapField = chartManager.shouldAggregate(type) ? 0 : labelField;
  const mapData = chartManager.shouldAggregate(type) ? chartManager.dataGroup : chartManager.data;
  const isGroupColor = chartManager.shouldAggregate(type); // && ['continuous', 'gradient', 'range'].includes(colors2.type);

  if (!mapData) return;

  if (DotTypes.includes(type)) {
    const legendStyle = cloneObject(legend.quillStyle);
    legendStyle.size = chartManager.getDynamicFontSize(legend.quillStyle);

    let legendContent = '';
    for (const row of mapData) {
      const dataRow = row['~meta'];
      const label = dataRow[labelField] as string;
      const legendQuillHTML = chartManager.getQuill(legend.customQuills, label, legend.quillValue);
      const legendQuill = chartManager.getHTMLandRect(legendQuillHTML, legendStyle, dataRow, chartManager.content, false, { color: legendStyle.color });

      dataRow.legendStr = legendQuill.html;
      dataRow.legendWidth = legendQuill.width;
      dataRow.legendHeight = legendQuill.height;

      // Dot Plot
      const customDot = dots.customDots.find((item: CustomDots) => item.id === row[0]);

      // Symbol
      const symbol = customDot ? customDot.symbol : dots.symbol;
      let svg = '';
      let _svg = '';
      let name = '';
      if (symbol) {
        name = `marker${capitalize(toCamelCase(symbol))}`;
        svg = Markers[name].data;
        _svg = svg.replace('<svg', "<svg style='position: relative; margin-right:5px' "); // Fix css Position
      }

      const html = `<div class="flex items-center">${_svg}${dataRow.legendStr}</div>`;
      legendContent = `${legendContent}${html}`;
    }

    // console.log("🚀 ~ legendContent:", legendContent)
    const legendRect = chartManager.getElementRect(`<div class="flex flex-wrap">${legendContent}</div>`, availableWidth, false, false, true, false);

    return {
      height: Math.ceil(legendRect.height),
      offset: heightMarginPadding,
    };
  }

  const enrichedData = mapData.map((obj: any) => {
    if (!obj) return;
    const keyFmt = chartManager.formatPipe.transform(obj[mapField], legend.format);
    const color = isGroupColor ? chartManager.groupedColorScale(obj['~meta']) : chartManager.colorScale(obj);

    // console.log('color', obj, key, color, isGroupColor, chartManager.colorScale);

    const fontStyle = getCSSFont(dynamicStyle);
    const { width, height } = chartManager.getElementRect(`<span style="font: ${fontStyle}">${keyFmt}</span>`, undefined, undefined, undefined, true);

    const canvasWidth = chartManager.getTextMetrics(keyFmt, dynamicStyle).width;

    obj['~meta'] = {
      ...(obj['~meta'] || {}),
      color: color2css(color),
      width: width,
    };

    return obj;
  });

  // console.log("enrichedData", enrichedData);

  const uniqueLabels = unique(mapData.map((obj) => obj[mapField]));
  const uniqueData = enrichedData.filter((obj, idx) => uniqueLabels.indexOf(obj[mapField]) === idx);

  const blockWidth = dynamicStyle.size * 1.2;
  const oneLineHeight = dynamicStyle.size * (fixLineHeight(dynamicStyle.lineheight.value) - 1);

  let currentLine = { width: 0, items: [] };
  const legendLines = [];
  for (const obj of uniqueData) {
    const itemWidth = obj['~meta'].width + blockWidth * 2;
    if (currentLine.width + itemWidth > availableWidth && currentLine.items.length > 0) {
      legendLines.push(currentLine);
      currentLine = { width: 0, items: [] };
    }

    const curLineHeight = legendLines.length * dynamicStyle.size * (fixLineHeight(dynamicStyle.lineheight.value) - 1);
    const curTextHeight = legendLines.length * dynamicStyle.size + curLineHeight + oneLineHeight;

    obj['~meta'].x = currentLine.width;
    obj['~meta'].y = curTextHeight;

    currentLine.width += itemWidth;
    currentLine.items.push(obj);
  }

  if (currentLine.width > 0) {
    legendLines.push(currentLine);
  }

  legendLines.forEach((line, idx) => {
    for (const obj of line.items) {
      obj['~meta'].lineNum = idx;
      obj['~meta'].lineWidth = line.width;
      obj['~meta'].xStart = (availableWidth - line.width) / 2;
    }
  });

  const lineHeight = legendLines.length * dynamicStyle.size * (fixLineHeight(dynamicStyle.lineheight.value) - 1);
  const textHeight = legendLines.length * dynamicStyle.size + lineHeight + oneLineHeight;

  const legendHeight = textHeight; // + (legend.scrollable ? 0 : heightMarginPadding);
  return { height: Math.ceil(legendHeight), offset: heightMarginPadding };
};

/**
 * [render legend to d3 context]
 */
export const renderLegend = (chartManager: ChartManager): void => {
  const { type, typeSpecific } = chartManager.content;
  const { labelField } = getOldFields(chartManager.content.type, chartManager.content?.typeSpecific?.dimensions);
  const { legend, values, animation, dots } = typeSpecific.chart;
  const { backgroundColor } = typeSpecific.style.background;
  const { colors2 } = typeSpecific.style;

  const mapField = chartManager.shouldAggregate(type) ? 0 : labelField;
  const mapData = chartManager.shouldAggregate(type) ? chartManager.dataGroup : chartManager.data;

  if (!mapData) return;

  const uniqueLabels = unique(mapData.map((obj) => obj[mapField]));
  const uniqueData = mapData.filter((obj, idx) => uniqueLabels.indexOf(obj[mapField]) === idx);
  const textColor = chroma(backgroundColor).hsl()[2] > values.lightnessThreshold ? values.darkColor : values.lightColor;

  const data: D3RectData[] = [];
  const textData: D3TextData[] = [];
  const htmlData: any[] = [];
  const symbolData: any[] = [];

  if (!legend.enabled || !chartManager.isValid) {
    d3renderRect(chartManager.legend.element, data);
    d3renderText(chartManager.legend.element, textData);
    return;
  }

  const dynamicStyle = cloneObject(legend.style);
  dynamicStyle.size = chartManager.getDynamicFontSize(legend.style, undefined, legend.fontScale);

  const { margin, padding } = dynamicStyle;
  const widthMargin = chartManager.getStyleInPx(margin, 'left', true) + chartManager.getStyleInPx(margin, 'right', true);
  const widthPadding = chartManager.getStyleInPx(padding, 'left', true) + chartManager.getStyleInPx(padding, 'right', true);
  const widthMarginPadding = widthMargin + widthPadding;

  const heightMargin = chartManager.getStyleInPx(margin, 'top', true) + chartManager.getStyleInPx(margin, 'bottom', true);
  const heightPadding = chartManager.getStyleInPx(padding, 'top', true) + chartManager.getStyleInPx(padding, 'bottom', true);
  const heightMarginPadding = heightMargin + heightPadding;

  const offsetTop = chartManager.getStyleInPx(margin, 'top', true) + chartManager.getStyleInPx(padding, 'top', true);
  const offsetBottom = chartManager.getStyleInPx(margin, 'bottom', true) + chartManager.getStyleInPx(padding, 'bottom', true);

  const offsetLeft = chartManager.getStyleInPx(margin, 'left', true) + chartManager.getStyleInPx(padding, 'left', true);
  const offsetRight = chartManager.getStyleInPx(margin, 'right', true) + chartManager.getStyleInPx(padding, 'right', true);

  for (const obj of uniqueData) {
    const label = obj[mapField];

    let color = chartManager.colorScale(obj);
    const isGroupColor = chartManager.shouldAggregate(type); // && ['continuous', 'gradient', 'range'].includes(colors2.type);

    if (isGroupColor) {
      const group = chartManager.dataGroup.find((groupObj) => groupObj[0] === label);
      if (group) color = chartManager.groupedColorScale(group['~meta']);
    }

    const hoverColor = color2css(chroma(color).darken());

    let xOffset = 0;
    const yOffset = 0; // + (legend.scrollable ? 0 : offsetTop);

    if (legend.position === 'left') xOffset = offsetLeft;
    if (legend.position === 'auto') xOffset = obj['~meta'].xStart + offsetLeft;
    if (legend.position === 'right') xOffset = chartManager.legend.width - obj['~meta'].lineWidth - offsetRight;

    const x = obj['~meta'].x + xOffset;
    const y = obj['~meta'].y + yOffset;

    const width = dynamicStyle.size;
    const height = dynamicStyle.size;

    const click = (event) => chartManager.openPopup(event, obj);
    const hover = (event, _, node) => select(node).attr('fill', hoverColor);
    const leave = (event, _, node) => select(node).attr('fill', color);

    const callbacks = { hover, leave };
    data.push({
      x: roundTo(x),
      y: roundTo(y),
      width: roundTo(width),
      height: roundTo(height),
      color: color2css(color),
      callbacks,
    });

    const fontStyle = String(legend.style.weight).includes('i') ? 'italic' : undefined;

    textData.push({
      text: chartManager.formatPipe.transform(label, legend.format),
      color: color2css(legend.style.color),
      fontSize: roundTo(dynamicStyle.size),
      fontStyle,
      fontWeight: String(legend.style.weight).replace('i', ''),
      fontFamily: legend.style.fontFamily,
      x: roundTo(x),
      y: roundTo(y),
      dx: '1.5em',
      dy: '0.85em',
      // callbacks: { click }
    });

    // Dot Plot
    const customDot = dots.customDots.find((item: CustomDots) => item.id === obj[0]);
    const size = customDot ? customDot.size : dots.size;
    // const offset = 11;
    const offset = width + 5;

    // Symbol
    const symbol = customDot ? customDot.symbol : dots.symbol;
    let svg = '';
    let _svg = '';
    let name = '';
    if (symbol) {
      name = `marker${capitalize(toCamelCase(symbol))}`;
      svg = Markers[name].data;
      _svg = svg.replace('<svg', "<svg style='position: relative; margin-right:5px' "); // Fix css Position
      _svg = _svg.replace('<path', `<path fill='${color2css(color)}' `);
    }

    const html = `<div class="flex items-center">${_svg}${obj['~meta'].legendStr}</div>`;
    const customQuill = legend.customQuills.find((quill: Quill) => quill.id === obj[0]);

    if (!customQuill?.hide) {
      htmlData.push({
        html,
        x: roundTo(x),
        y: roundTo(y),
        width: obj['~meta'].legendWidth + size + offset,
        height: obj['~meta'].legendHeight,
        color: color2css(legend.style.color),
        class: { flex: true },
        callbacks: {},
        // justifyContent: 'flex-end',
        alignItems: 'center',
        animate: {
          enabled: animation.enabled,
          delay: animation.delay,
          duration: animation.duration,
        },
        // outline: '1px solid #d22ed2', // DEBUG
      });
    }
  }

  if (!DotTypes.includes(type)) {
    d3renderRect(chartManager.legend.element, data);
    d3renderText(chartManager.legend.element, textData);
    overlayText(chartManager.legendOverlay, [], '.overlay-legend');
  } else {
    // Symbol
    const container = chartManager.legendOverlay.element;
    const overlay = container.select('.overlay-legend').node() as HTMLElement;
    overlay.style.marginLeft = `${offsetLeft}px`;
    overlay.style.marginRight = `${offsetRight}px`;
    if (legend.position === 'left') overlay.style.justifyContent = 'flex-start';
    if (legend.position === 'auto') overlay.style.justifyContent = 'center';
    if (legend.position === 'right') overlay.style.justifyContent = 'flex-end';
    overlayText(chartManager.legendOverlay, htmlData, '.overlay-legend');
    d3renderRect(chartManager.legend.element, []);
    d3renderText(chartManager.legend.element, []);
  }
};
